import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

/* ── Theme ─────────────────────────────────────────────────── */
const GOLD       = '#C9952A';
const GOLD_LIGHT = '#E8B84B';
const GOLD_DARK  = '#9A6F1A';
const CREAM      = '#FAF6ED';
const WARM_WHITE = '#FFFCF5';
const TEXT       = '#2A1F0E';
const TEXT_MID   = '#6B5540';

const shimmer = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/* ── Icon helpers ───────────────────────────────────────────── */
const IconWrap = styled.div`
  width: 82px;
  height: 82px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(201,149,42,0.1), rgba(232,184,75,0.06));
  border: 1.5px solid rgba(201,149,42,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.1rem;
  flex-shrink: 0;
`;


/* ── Containers ─────────────────────────────────────────────── */
const HomeContainer = styled.div`
  background: ${CREAM};
  overflow: hidden;
`;

/* ── HERO ───────────────────────────────────────────────────── */
const Hero = styled.section`
  position: relative;
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 2rem;
  background: ${CREAM};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 20% 30%, rgba(201,149,42,0.14) 0%, transparent 55%),
      radial-gradient(ellipse at 80% 70%, rgba(232,184,75,0.10) 0%, transparent 50%),
      radial-gradient(ellipse at 50% 10%, rgba(201,149,42,0.07) 0%, transparent 45%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cellipse cx='40' cy='12' rx='9' ry='20' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(0%2C40%2C40)'/%3E%3Cellipse cx='40' cy='12' rx='9' ry='20' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(45%2C40%2C40)'/%3E%3Cellipse cx='40' cy='12' rx='9' ry='20' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(90%2C40%2C40)'/%3E%3Cellipse cx='40' cy='12' rx='9' ry='20' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(135%2C40%2C40)'/%3E%3C/svg%3E");
    pointer-events: none;
    opacity: 0.8;
  }
`;

const HeroContent = styled.div`
  max-width: 860px;
  text-align: center;
  position: relative;
  z-index: 1;
  padding: 2rem;
`;

const LogoImg = styled.img`
  height: 180px;
  width: auto;
  margin-bottom: 1.6rem;
  filter: drop-shadow(0 4px 20px rgba(201,149,42,0.30));
`;

const Title = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.6rem, 5vw, 4rem);
  font-weight: 800;
  color: ${TEXT};
  margin-bottom: 1.4rem;
  line-height: 1.15;
  background: linear-gradient(135deg, #9A6F1A 0%, ${GOLD} 35%, ${GOLD_LIGHT} 65%, ${GOLD_DARK} 100%);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 8s ease infinite;
`;

const HeroDescription = styled.p`
  font-size: 1.15rem;
  color: ${TEXT_MID};
  line-height: 1.9;
  max-width: 600px;
  margin: 0 auto;
  font-family: 'Source Serif 4', serif;
`;

const GoldRule = styled.div`
  width: 80px;
  height: 2px;
  background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
  margin: 1.8rem auto;
  border-radius: 2px;
`;

/* ── Section shell ──────────────────────────────────────────── */
const Section = styled.section`
  padding: 5rem 2rem;
  background: ${({ $alt }) => $alt ? WARM_WHITE : CREAM};
`;

const SectionContent = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
`;

const SectionLabel = styled.p`
  font-family: 'Source Serif 4', serif;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${GOLD};
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const SectionTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  color: ${TEXT};
  margin-bottom: 0.8rem;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 56px;
    height: 2px;
    background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
    border-radius: 2px;
    margin: 0.9rem auto 0;
  }
`;

const SectionDesc = styled.p`
  font-size: 1.05rem;
  color: ${TEXT_MID};
  max-width: 700px;
  margin: 1.5rem auto 3rem;
  line-height: 1.85;
  font-family: 'Source Serif 4', serif;
`;

/* ── Feature grid ───────────────────────────────────────────── */
const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-top: 2.5rem;
  text-align: left;
`;

const FeatureCard = styled.div`
  background: ${WARM_WHITE};
  border-radius: 18px;
  padding: 2rem 1.8rem;
  border-top: 3px solid ${GOLD};
  box-shadow: 0 6px 24px rgba(201,149,42,0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 14px 36px rgba(201,149,42,0.14);
  }

  h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.2rem;
    color: ${TEXT};
    margin-bottom: 0.55rem;
  }

  p {
    color: ${TEXT_MID};
    font-size: 0.95rem;
    line-height: 1.75;
    font-family: 'Source Serif 4', serif;
  }
`;

/* ── Product cards ──────────────────────────────────────────── */
const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.8rem;
  margin-top: 3rem;
  text-align: left;
`;

const ProductCard = styled(Link)`
  background: ${WARM_WHITE};
  border-radius: 22px;
  padding: 2.4rem 2rem;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  border: 1px solid rgba(201,149,42,0.18);
  box-shadow: 0 8px 28px rgba(201,149,42,0.08);
  transition: all 0.35s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
    border-radius: 22px 22px 0 0;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 48px rgba(201,149,42,0.18);
    border-color: rgba(201,149,42,0.35);
  }

  h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.9rem;
    font-weight: 800;
    background: linear-gradient(135deg, ${GOLD_DARK}, ${GOLD}, ${GOLD_LIGHT});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: 0.95rem;
    color: ${TEXT_MID};
    line-height: 1.65;
    font-family: 'Source Serif 4', serif;
  }
`;

/* ── Component ──────────────────────────────────────────────── */
const Home = () => (
  <HomeContainer>
    <Hero>
      <HeroContent>
        <LogoImg src="/Bherilogo1.png" alt="BHERI Technology Foundation" />
        <Title>Bridging Heritage with Technology</Title>
        <GoldRule />
        <HeroDescription>
          India meets Bharat
        </HeroDescription>
      </HeroContent>
    </Hero>

    {/* ── MISSION ─────────────────────────────────────────── */}
    <Section $alt>
      <SectionContent>
        <SectionLabel>Who We Are</SectionLabel>
        <SectionTitle>Our Mission</SectionTitle>
        <SectionDesc>
          Dedicated to preserving and promoting India's rich cultural heritage through innovative technological solutions — making ancient wisdom accessible to the modern world.
        </SectionDesc>
        <FeatureGrid>
          <FeatureCard>
            {/* Archive / preservation */}
            <IconWrap>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 8v13H3V8"/><rect x="1" y="3" width="22" height="5" rx="1"/><line x1="10" y1="12" x2="14" y2="12"/>
              </svg>
            </IconWrap>
            <h3>Digital Preservation</h3>
            <p>Utilizing cutting-edge technology to preserve and document India's cultural treasures for future generations.</p>
          </FeatureCard>
          <FeatureCard>
            {/* Globe / accessibility */}
            <IconWrap>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </IconWrap>
            <h3>Accessibility</h3>
            <p>Making heritage content accessible to everyone through intuitive, user-friendly platforms.</p>
          </FeatureCard>
          <FeatureCard>
            {/* Lightbulb / innovation */}
            <IconWrap>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/>
                <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
              </svg>
            </IconWrap>
            <h3>Innovation</h3>
            <p>Combining traditional knowledge with modern technology for enhanced learning and discovery experiences.</p>
          </FeatureCard>
        </FeatureGrid>
      </SectionContent>
    </Section>

    {/* ── WHY BHERI ───────────────────────────────────────── */}
    <Section>
      <SectionContent>
        <SectionLabel>Why BHERI</SectionLabel>
        <SectionTitle>Built on Trust</SectionTitle>
        <FeatureGrid>
          <FeatureCard>
            {/* Shield / authentic */}
            <IconWrap>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <polyline points="9 12 11 14 15 10"/>
              </svg>
            </IconWrap>
            <h3>Authentic Content</h3>
            <p>Carefully curated and verified content from reliable, academically grounded sources.</p>
          </FeatureCard>
          <FeatureCard>
            {/* CPU chip / modern tech */}
            <IconWrap>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="6" height="6"/>
                <path d="M9 1v2M15 1v2M9 21v2M15 21v2M1 9h2M1 15h2M21 9h2M21 15h2"/>
                <rect x="3" y="3" width="18" height="18" rx="2"/>
              </svg>
            </IconWrap>
            <h3>Modern Technology</h3>
            <p>State-of-the-art tools and platforms delivering the best user experience.</p>
          </FeatureCard>
          <FeatureCard>
            {/* Users / expert team */}
            <IconWrap>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </IconWrap>
            <h3>Expert Team</h3>
            <p>Collaboration between heritage scholars and technology professionals.</p>
          </FeatureCard>
          <FeatureCard>
            {/* Search / research backed */}
            <IconWrap>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                <line x1="11" y1="8" x2="11" y2="14"/>
                <line x1="8" y1="11" x2="14" y2="11"/>
              </svg>
            </IconWrap>
            <h3>Research Backed</h3>
            <p>Every solution developed through extensive research, testing, and validation.</p>
          </FeatureCard>
        </FeatureGrid>
      </SectionContent>
    </Section>

    {/* ── PRODUCTS ────────────────────────────────────────── */}
    <Section $alt>
      <SectionContent>
        <SectionLabel>Explore</SectionLabel>
        <SectionTitle>Our Products</SectionTitle>
        <SectionDesc>
          A suite of innovative solutions designed to preserve, explore, and celebrate Indian heritage.
        </SectionDesc>
        <ProductGrid>
          <ProductCard to="/dhara">
            <IconWrap>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                <line x1="9" y1="7" x2="15" y2="7"/><line x1="9" y1="11" x2="15" y2="11"/>
              </svg>
            </IconWrap>
            <h3>Dhārā</h3>
            <p>Data API Service &amp; Example Apps — Discover intelligent Indic content APIs with 3.5L+ dictionary entries.</p>
          </ProductCard>
          <ProductCard to="/anubhuti">
            <IconWrap>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </IconWrap>
            <h3>Anubhūti</h3>
            <p>Interactive &amp; Immersive Experience — Transform heritage sites into living digital experiences.</p>
          </ProductCard>
          <ProductCard to="/abhyas">
            <IconWrap>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z"/>
                <path d="M6 3a3 3 0 0 0-3 3v12a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z"/>
                <path d="M13 6H3"/><path d="M21 6h-3"/><path d="M13 12H3"/><path d="M21 12h-3"/>
                <path d="M13 18H3"/><path d="M21 18h-3"/>
              </svg>
            </IconWrap>
            <h3>Abhyās</h3>
            <p>Movement Intelligence Platform — Real-time markerless IK, Digital Twin, and Text-to-Movement AI.</p>
          </ProductCard>
          <ProductCard to="/ranjana">
            <IconWrap>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <line x1="6" y1="12" x2="10" y2="12"/>
                <line x1="8" y1="10" x2="8" y2="14"/>
                <line x1="15" y1="13" x2="15.01" y2="13" strokeWidth="3"/>
                <line x1="18" y1="11" x2="18.01" y2="11" strokeWidth="3"/>
                <rect x="2" y="6" width="20" height="12" rx="3"/>
              </svg>
            </IconWrap>
            <h3>Rañjanā</h3>
            <p>Heritage Gaming &amp; Entertainment — Play and learn about Indian culture through interactive stories.</p>
          </ProductCard>
        </ProductGrid>
      </SectionContent>
    </Section>
  </HomeContainer>
);

export default Home;
